/**
 * src/components/agent/AgentShellTrigger.jsx — FAB + launcher for AgentShell.
 *
 * Phase 82-03 — Chat System Unification, wave 2.
 *
 * Mounts a fixed bottom-right Bot FAB on every non-agent view.
 * On click, captures the current view-context-change detail
 * (so the CEO's scope is frozen at invocation time) and opens AgentShell in
 * modal mode, seeded with ctxOverride. Hides on `/agent` tab since the full
 * page AgentShell is already mounted.
 *
 * Listens to the same `view-context-change` bus AppShell dispatches. Also
 * handles the legacy `open-agent-chat` event (dispatched by quick-capture +
 * CTA buttons across the app) by opening the modal with the event detail as
 * override.
 *
 * Hooks at top of body per Phase 90-07 rule. Tokens only per HARD DEPLOYMENT.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Bot } from 'lucide-react';
import AgentShell from './AgentShell';
import { useT } from '../../lib/i18n/I18nContext';
import useBreakpoint from '../../hooks/useBreakpoint';
import { stashAgentOpen } from '../../lib/agent/pendingAgentOpen';

const EMPTY_DETAIL = {
  view: null,
  projectId: null,
  dealId: null,
  taskId: null,
  personId: null,
  projectName: null,
  dealName: null,
  taskName: null,
  personName: null,
};

// Bottom offset computed per breakpoint. On desktop there is no bottom nav,
// so 20px from the viewport bottom is clear space. On mobile/tablet the
// bottom tab bar occupies the bottom 56px + safe-area — the FAB must clear
// it by at least 20px AND sit above any per-view FAB (Procurement's New-PO
// FAB is at bottom: 72). Bug 4 fix 2026-04-24 (CEO report: FAB overlapping
// nav taps on Procurement/More).
// 56 (nav) + 72 (Procurement FAB) + 56 (this FAB height) + 12 (gap) = 196 on mobile.
// TICKET-752: 196px cleared Procurement's own New-PO FAB, but it was applied on
// EVERY view — so on Pipeline the button floated in the middle of the deal list, on
// top of a card ("there is a blue hidden ball? Overlay is a bad practice"). Only the
// views that actually stack a second FAB pay the tall offset.
const VIEWS_WITH_OWN_FAB = new Set(['procurement']);

function fabStyle(isDesktop, activeView) {
  const stacked = VIEWS_WITH_OWN_FAB.has(activeView);
  return {
    position: 'fixed',
    right: 20,
    bottom: isDesktop
      ? 20
      : `calc(${stacked ? 196 : 76}px + env(safe-area-inset-bottom, 0px))`,
    zIndex: 900,
    width: 56,
    height: 56,
    minWidth: 56,
    minHeight: 56,
    borderRadius: '50%',
    background: 'var(--accent)',
    color: 'var(--on-accent, #FFF)',
    border: 'none',
    cursor: 'pointer',
    boxShadow: 'var(--shc, 0 4px 12px rgba(0,0,0,0.15))',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'inherit',
    transition: 'transform 0.15s ease-out',
  };
}

const modalBackdropStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

const modalPanelStyle = {
  width: '100%',
  maxWidth: 1100,
  height: '100%',
  maxHeight: 'min(880px, 95vh)',
  background: 'var(--bg)',
  borderRadius: 14,
  overflow: 'hidden',
  boxShadow: 'var(--shc, 0 12px 40px rgba(0,0,0,0.25))',
  border: '1px solid var(--bdr)',
};

export default function AgentShellTrigger({ activeView }) {
  const { t } = useT();
  const { isDesktop } = useBreakpoint();
  const detailRef = useRef(EMPTY_DETAIL);
  const [open, setOpen] = useState(false);
  const [override, setOverride] = useState(null);

  useEffect(() => {
    const handler = e => {
      const d = e?.detail || {};
      detailRef.current = {
        view: d.view ?? null,
        projectId: d.projectId ?? null,
        dealId: d.dealId ?? null,
        taskId: d.taskId ?? null,
        personId: d.personId ?? null,
        projectName: d.projectName ?? null,
        dealName: d.dealName ?? null,
        taskName: d.taskName ?? null,
        personName: d.personName ?? null,
      };
    };
    window.addEventListener('view-context-change', handler);
    return () => window.removeEventListener('view-context-change', handler);
  }, []);

  useEffect(() => {
    const handler = e => {
      const d = e?.detail || {};
      setOverride({
        view: d.view ?? detailRef.current.view,
        projectId: d.projectId ?? detailRef.current.projectId,
        dealId: d.dealId ?? detailRef.current.dealId,
        taskId: d.taskId ?? detailRef.current.taskId,
        personId: d.personId ?? detailRef.current.personId,
        projectName: d.projectName ?? detailRef.current.projectName,
        dealName: d.dealName ?? detailRef.current.dealName,
        taskName: d.taskName ?? detailRef.current.taskName,
        personName: d.personName ?? detailRef.current.personName,
        // TICKET-912: Quote Copilot launchers (builder header, deal Quotes/Bundles tabs)
        // open THIS bot in copilot mode. Functions ride in the event detail (same window).
        copilot: d.copilot,
        quoteId: d.quoteId ?? null,
        onBeforeTurn: d.onBeforeTurn,
        onChanged: d.onChanged,
        aboveBuilder: !!d.aboveBuilder,
      });
      setOpen(true);
    };
    // TICKET-1499: AppShell turns `open-agent-chat` into the Agent tab, which mounts
    // after this event -- hand it the scope (and conversation) the caller asked for.
    const stash = e => stashAgentOpen(e?.detail || null);
    window.addEventListener('open-agent-chat', stash);
    window.addEventListener('open-agent-chat', handler);
    // TICKET-912: the Quote Copilot launchers use their own event -- AppShell turns
    // `open-agent-chat` into a switch to the full-page Agent tab, where this FAB is gone.
    window.addEventListener('open-quote-copilot', handler);
    return () => {
      window.removeEventListener('open-agent-chat', stash);
      window.removeEventListener('open-agent-chat', handler);
      window.removeEventListener('open-quote-copilot', handler);
    };
  }, []);

  const handleOpen = useCallback(() => {
    setOverride({ ...detailRef.current, view: activeView || detailRef.current.view });
    setOpen(true);
  }, [activeView]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  if (activeView === 'agent') return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          style={fabStyle(isDesktop, activeView)}
          onClick={handleOpen}
          aria-label={t('agentChat.openAgentChat')}
          data-testid="agent-shell-trigger"
        >
          <Bot size={24} />
        </button>
      )}

      {open && (
        <div
          // The quote builder is a zIndex-10000 portal; opened from it, the bot must sit above.
          style={override?.aboveBuilder ? { ...modalBackdropStyle, zIndex: 10020 } : modalBackdropStyle}
          onClick={e => {
            if (e.target === e.currentTarget) handleClose();
          }}
          role="button"
          tabIndex={0}
          aria-label={t('agentChat.closeChatList')}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              e.preventDefault();
              handleClose();
            }
          }}
          data-testid="agent-shell-modal-backdrop"
        >
          <div
            style={modalPanelStyle}
            role="dialog"
            aria-modal="true"
            aria-label={t('agentChat.openAgentChat')}
          >
            <AgentShell
              view="agent"
              mode="modal"
              ctxOverride={override}
              onClose={handleClose}
            />
          </div>
        </div>
      )}
    </>
  );
}
